import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useGetLocationConfigurations } from 'src/api/queries/microservices/platform-markets-location-config'
import { useCompanyContext } from 'src/companies/routes/company-context'
import { Box, Button, Placeholders, Row, Table2, useTable } from 'src/ui'
import { getEnvironment } from 'src/utils'

function getSortOrder(configurations: Array<{ key: string; value: string }>) {
  return configurations?.find((configuration) => configuration.key === 'sortOrder')?.value ?? ''
}

export default function PlatformLocationsIndex() {
  const { company } = useCompanyContext()
  const { data, status } = useGetLocationConfigurations()

  const tableData = useMemo(
    () =>
      (data ?? []).map((location) => ({
        ...location,
        sortOrder: getSortOrder(location.configurations),
      })),
    [data]
  )

  const columns = useMemo(
    () => [
      {
        id: 'locationName',
        accessor: 'locationName',
        Header: 'Name',
        disableSortBy: true,
      },
      {
        id: 'sortOrder',
        accessor: 'sortOrder',
        Header: 'Sort Order',
        disableSortBy: true,
      },
      {
        id: '_actions',
        Header: '',
        accessor: '_actions',
        disableSortBy: true,
        Cell: ({ row }) => (
          <Row justifyContent="flex-end" alignItems="center">
            <Button
              size="sm"
              variant="ghost"
              color="primary.500"
              as={Link}
              to={[
                null,
                'companies',
                getEnvironment(),
                company.slug,
                'markets',
                'platform-locations',
                row.original.locationId,
              ].join('/')}
            >
              Edit
            </Button>
          </Row>
        ),
      },
    ],
    [company.slug]
  )

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({
    data: tableData,
    tableOptions: {
      initialState: {
        sortBy: [],
      },
    },
    columns,
  })

  if (status === 'pending') return <Placeholders.LoadingState mt={8} />
  if (status === 'error') return <Placeholders.FailedState mt={8} />

  return (
    <Box>
      {tableData.length === 0 ? (
        <Placeholders.EmptyState message="No platform locations found" />
      ) : (
        <Box bg="white" mb={4} pt={3} borderRadius={3} boxShadow="md">
          <Table2 {...getTableProps()} striped>
            <Table2.Header headerGroups={headerGroups} />
            <Table2.Body
              rows={rows}
              getTableBodyProps={getTableBodyProps}
              prepareRow={prepareRow}
            />
          </Table2>
        </Box>
      )}
    </Box>
  )
}
